import React, { createContext, useCallback, useContext, useMemo, useReducer } from 'react';

const initialState = {
	showSaveDialog: false,
	designToLoad: null,
	designLoadRevision: 0,
	programmaticDesignLoadRevision: 0,
	isMetadataExpanded: true,
	isLoadDesignJsonDialogOpen: false,
	loadDesignJsonValue: '',
	loadDesignJsonError: '',
};

const reducer = (state, action) => {
	switch (action.type) {
		case 'toggleMetadata':
			return {
				...state,
				isMetadataExpanded: !state.isMetadataExpanded,
			};
		case 'openSaveDialog':
			return {
				...state,
				showSaveDialog: true,
			};
		case 'closeSaveDialog':
			return {
				...state,
				showSaveDialog: false,
			};
		case 'setDesignToLoad':
			return {
				...state,
				designToLoad: action.payload,
				designLoadRevision: state.designLoadRevision + 1,
			};
		case 'markProgrammaticDesignLoaded':
			return {
				...state,
				programmaticDesignLoadRevision: state.programmaticDesignLoadRevision + 1,
			};
		case 'openLoadDesignDialog':
			return {
				...state,
				isLoadDesignJsonDialogOpen: true,
				loadDesignJsonError: '',
			};
		case 'closeLoadDesignDialog':
			return {
				...state,
				isLoadDesignJsonDialogOpen: false,
			};
		case 'setLoadDesignJsonValue':
			return {
				...state,
				loadDesignJsonValue: action.payload,
				loadDesignJsonError: action.clearError ? '' : state.loadDesignJsonError,
			};
		case 'setLoadDesignJsonError':
			return {
				...state,
				loadDesignJsonError: action.payload,
			};
		default:
			return state;
	}
};

const UnlayerUIContext = createContext(null);

/**
 * Manages local UI state for the Unlayer compose editor shell: modal visibility,
 * editor design loads, metadata panel expansion, and JSON import state.
 */
export const UnlayerUIProvider = ({ children }) => {
	const [state, dispatch] = useReducer(reducer, initialState);

	const toggleMetadata = useCallback(() => {
		dispatch({ type: 'toggleMetadata' });
	}, []);

	const openSaveDialog = useCallback(() => {
		dispatch({ type: 'openSaveDialog' });
	}, []);

	const closeSaveDialog = useCallback(() => {
		dispatch({ type: 'closeSaveDialog' });
	}, []);

	const setDesignToLoad = useCallback((design) => {
		dispatch({ type: 'setDesignToLoad', payload: design });
	}, []);

	const markProgrammaticDesignLoaded = useCallback(() => {
		dispatch({ type: 'markProgrammaticDesignLoaded' });
	}, []);

	const openLoadDesignDialog = useCallback(() => {
		dispatch({ type: 'openLoadDesignDialog' });
	}, []);

	const closeLoadDesignDialog = useCallback(() => {
		dispatch({ type: 'closeLoadDesignDialog' });
	}, []);

	const setLoadDesignJsonValue = useCallback((value, { clearError = false } = {}) => {
		dispatch({
			type: 'setLoadDesignJsonValue',
			payload: value,
			clearError,
		});
	}, []);

	const setLoadDesignJsonError = useCallback((message) => {
		dispatch({ type: 'setLoadDesignJsonError', payload: message });
	}, []);

	const value = useMemo(
		() => ({
			...state,
			closeLoadDesignDialog,
			closeSaveDialog,
			markProgrammaticDesignLoaded,
			openLoadDesignDialog,
			openSaveDialog,
			setDesignToLoad,
			setLoadDesignJsonError,
			setLoadDesignJsonValue,
			toggleMetadata,
		}),
		[
			state,
			closeLoadDesignDialog,
			closeSaveDialog,
			markProgrammaticDesignLoaded,
			openLoadDesignDialog,
			openSaveDialog,
			setDesignToLoad,
			setLoadDesignJsonError,
			setLoadDesignJsonValue,
			toggleMetadata,
		],
	);

	return <UnlayerUIContext.Provider value={value}>{children}</UnlayerUIContext.Provider>;
};

export const useUnlayerUIContext = () => {
	const context = useContext(UnlayerUIContext);
	if (!context) {
		throw new Error('useUnlayerUIContext must be used within an UnlayerUIProvider');
	}
	return context;
};
